<template>
  <div class="student-record">
    <v-row>
      <v-spacer></v-spacer>
      <v-col cols="12" md="3" sm="4">
        <v-btn color="primary" :to="{ name: 'AddClassroom' }">Add Classroom</v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="12" md="12" lg="12">
        <!-- Change lg to 9 if another component is placed on the right  -->
        <v-card class="mx-4 my-4">
          <v-card-title>
            Classroom Record
            <v-spacer></v-spacer>

            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
          </v-card-title>
          <v-row>
            <v-col cols="6" md="4">
              <v-select
                v-model="filter.academic_session"
                :items="currentInstitution.academic_sessions"
                small-chips
                outlined
                color="primary"
                label="Filter by Academic Year"
                hide-details
                dense
                background-color="transparent"
                item-value="id"
                item-text="title"
                class="mb-4 ml-4 rounded-input"
                @change="getAllClassRooms()"
              ></v-select>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="6" md="4" class="text-right mr-2">
              <download-excel
                v-if="currentUser.privileges.canExportClassrooms"
                :fetch="getExportData"
                :fields="export_fields"
                :name="getExportName"
              >
                <v-btn
                  class="ma-2"
                  :loading="export_in_progress"
                  :disabled="export_in_progress"
                  color="white"
                  elevation="1"
                >
                  Export
                  <v-icon right dark> mdi-export </v-icon>
                  <template v-slot:loader>
                    <span class="custom-loader">
                      <v-icon light>mdi-cached</v-icon>
                    </span>
                  </template>
                </v-btn>
              </download-excel></v-col
            >
          </v-row>
          <v-data-table
            :headers="headers"
            :items="classrooms"
            :search="search"
            :options.sync="options"
            :items-per-page="15"
            :multi-sort="true"
            :server-items-length="pagination.totalCount"
            @click:row="clickClassRoomRow"
            class="row-pointer"
            id="class-data-table"
          >
            <template v-slot:item.sections="{ item }">
              <h5>{{ item.sections || "-" }}</h5>
            </template>
            <template v-slot:item.room_no="{ item }">
              <h5>{{ item.room_no || "-" }}</h5>
            </template>
            <template v-slot:item.room_subjects="{ item }">
              <v-chip color="#642B73" class="text-white"
                ><h5>{{ item.room_subjects }} Subjects</h5></v-chip
              >
            </template>
            <template v-slot:item.room_users="{ item }">
              <v-chip color="#FE3B40" class="text-white"
                ><h5>{{ item.room_users }} Students</h5></v-chip
              >
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import api from "@api/index";
import endpoints from "@api/repository";
import JsonExcel from "vue-json-excel";
import Helper from "@utils/misc";
import TourHelper from "@utils/tour_helper";


export default {
  name: "ClassroomRecord",
  data() {
    return {
      search: "",
      options: {},
      pagination: {
        totalCount: 0,
        url: endpoints.getAllClassRooms,
      },
      headers: [
        {
          text: "Classroom",
          align: "start",
          sortable: false,
          value: "sections",
        },
        { text: "Room No.", value: "room_no", sortable: false },
        { text: "Total Subjects", value: "room_subjects" },
        { text: "Total Students", value: "room_users" },
      ],
      filter: {
        academic_session: null,
      },
      classrooms: [],
      snackbar: {
        visible: false,
        text: "",
      },
      export_fields: {
        "Room no": "room_no",
        Title: "sections",
      },
      export_in_progress: false,
    };
  },
  computed: {
    ...mapGetters(["accessToken", "currentUser", "currentInstitution"]),
    getExportName() {
      return Helper.getExportName();
    },
    apiEssentials() {
      return {
        accessToken: this.accessToken,
        handleErrorsFunction: this.handleApiErrors,
        setLoading: this.setLoading,
      };
    },
  },
  components: {
    downloadExcel: JsonExcel,
  },
  methods: {
    ...mapActions(["setLoading"]),

    handleTour() {
      var steps = [];
      steps.push({id: '1', title: "Welcome to Classroom Section!", description: "Let's start with a quick tour!", videoLink: "https://youtube.com"});
      steps.push({id: '2', title: "Classroom Table", description: "All Classroom related Information are accessible here. Click at a row for more information about a Classroom", el: '#class-data-table',position:'top'});
      TourHelper.startTour(this, "classroom", steps);
    },

    getParamsString() {
      // get url var
      var params = "&o=";
      // iterate over sortBy options and add sortby and sortdesc seperated with commas
      for (var sortOption in this.options.sortBy) {
        params +=
          (this.options.sortDesc[sortOption] ? "" : "-") +
          this.options.sortBy[sortOption];
        if (sortOption != this.options.sortBy.length - 1) params += ",";
      }

      if (params.includes("room_subjects")) {
        params = params.replace("room_subjects", "room_subjects_count");
      }
      if (params.includes("room_users")) {
        params = params.replace("room_users", "room_users_count");
      }
      return params;
    },

    // get all classroom from the server
    async getAllClassRooms() {
      var response;
      var url = this.pagination.url;
      // if search query exists, add to the url as a GET param w/o disturbing other params
      if (this.search) {
        url += (url.split("?")[1] ? "&" : "?") + "query=" + this.search;
      }

      if (this.filter.academic_session) {
        url = Helper.addUrlParams(
          url,
          "session=" + this.filter.academic_session
        );
      }

      response = await api.handleApi({
        apiEssentials: this.apiEssentials,
        url: url,
        responseVar: response,
      });

      if (response) {
        // console.log(response);
        this.classrooms = response.results;
        this.pagination.totalCount = response.count;
        this.pagination.nextUrl = response.next;
      }
    },

    async getExportData() {
      this.export_in_progress = true;
      var response;
      var url = endpoints.getAllClassRooms + "?extract=true";

      if (this.search) {
        this.export_url += "&query=" + this.search;
      }

      response = await api.handleApi({
        apiEssentials: this.apiEssentials,
        url: url,
        responseVar: response,
      });

      if (response) {
        this.export_in_progress = false;
        return response;
      }
    },

    async handleApiErrors(err) {
      this.setLoading(false);
      if (err.response) {
        console.log(err.response);
      } else {
        this.snackbar.text = "Unknown error occurred";
        this.snackbar.visible = true;
      }
    },

    // handle row click
    clickClassRoomRow(value) {
      this.$router.push({ name: "ClassroomDetails", params: { id: value.id } });
    },
  },
  watch: {
    options: {
      async handler() {
        // console.log("something changed");
        // console.log(this.options);
        this.pagination.url =
          endpoints.getAllClassRooms + "?page=" + this.options.page;
        if (this.options.sortBy.length > 0) {
          var params = this.getParamsString();

          // handles count ordering

          this.pagination.url += params;
        }
        this.getAllClassRooms();
      },
    },
    search: {
      async handler() {
        this.options.page = 1;
        var searched = this.search;
        var self = this;
        setTimeout(function () {
          if (searched == self.search) {
            self.getAllClassRooms();
          }
        }, 500);
      },
    },
  },
  created() {
    this.handleTour();
    this.filter.academic_session = this.currentInstitution.academic_sessions.find(
      (s) => s.active
    ).id;
    // this.getAllClassRooms(); <- getting called in watch options
  },
};
</script>

<style scoped>
.text-white {
  color: white;
}

.row-pointer >>> tbody tr :hover {
  cursor: pointer;
}
</style>